// tslint:disable:restrict-plus-operands
import Fuse from 'fuse.js';
import React, { useState } from 'react';

import { Button, Input, Link, ListTable, LoadingSpinner, Modal, Pagination, Text } from '@opengov/component-library';

import styles from './styles.module.scss';

export const ENTITY_SEARCH_FIELDS = ['id', 'name', 'state', 'country', 'subdomain', 'display_name', 'entity_type'];

export interface IEntitySwitcherNoResults {
  showCreateEntity?: boolean;
  onCreateEntity?(): void;
}

export const EntitySwitcherNoResults = ({ showCreateEntity, onCreateEntity }) => (
  <div className={styles.noResultsWrapper}>
    <Text size="small" font="bold">
      No entity was found with this filter.
    </Text>
    {showCreateEntity ? (
      <>
        <Text size="small">Try another search or...</Text>
        <Button variant="primary" onClick={onCreateEntity}>
          Create Entity
        </Button>
      </>
    ) : null}
  </div>
);

export const EntitySettingButton = ({ entitySubdomain }) => {
  const onEntitySetting = (event) => {
    event.preventDefault();
    (window as any).location = `/entity/${entitySubdomain}/settings`;
  };
  return (
    <div className={styles.entitySettingBtn}>
      <Button size="small" dataTest="entity-setting-button" onClick={onEntitySetting}>
        Entity Settings
      </Button>
    </div>
  );
};

export interface IEntitySwitcherItemProps {
  name: string;
  state: string;
  country: string;
  type: string;
  id: string;
  link: string;
  showEntitySettingsButton?: boolean;
}

export const EntitySwitcherItem: React.FC<IEntitySwitcherItemProps> = ({
  name,
  state,
  country,
  type,
  id,
  link,
  showEntitySettingsButton
}) => (
  <ListTable.Row href={link} ariaLabel={`Navigate to ${name} entity`}>
    <ListTable.Cell>
      <b>{name}</b>, {state}, {country}
    </ListTable.Cell>
    <ListTable.Cell>{type}</ListTable.Cell>
    <ListTable.Cell>{id}</ListTable.Cell>
    {showEntitySettingsButton && <EntitySettingButton entitySubdomain={id} />}
  </ListTable.Row>
);
export interface IEntitySwitcherItem {
  id: string;
  name: string;
  subdomain: string;
  state: string;
  country: string;
  entity_type?: string;
}

export interface IEntitySwitcherProps {
  canCreateEntity?: boolean;
  createEntityLink?: string;
  currentAppPathname?: string;
  currentPagePath?: string;
  showEntitySettingsButton?: boolean;
  shouldFetchEntities?: boolean;
  entities?: IEntitySwitcherItem[];
  onCreateEntity?(): void;
  onClose?(): void;
}

export const EntitySwitcher: React.FC<IEntitySwitcherProps> = ({
  canCreateEntity = false,
  createEntityLink,
  entities,
  currentAppPathname,
  currentPagePath,
  onClose,
  onCreateEntity,
  showEntitySettingsButton
}) => {
  const defaultPageSize = 10;
  const [searchText, setSearchText] = useState('');
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const paginationStartingCursor = (currentPageNumber - 1) * pageSize;
  const paginationEndingCursor = currentPageNumber * pageSize;

  let filteredEntities = [];

  const fuse = new Fuse(entities, { keys: ENTITY_SEARCH_FIELDS, threshold: 0.2 });

  if (searchText.length) {
    filteredEntities = fuse.search(searchText);
  } else {
    filteredEntities = entities;
  }

  const isLoading = !filteredEntities.length && !searchText;
  const noResults = !filteredEntities.length && searchText;
  const pagePath = currentPagePath || '';

  const entitySwitcherItems = filteredEntities.slice(paginationStartingCursor, paginationEndingCursor).map((entity) => {
    return (
      <EntitySwitcherItem
        key={entity.id}
        name={entity.name}
        state={entity.state}
        country={entity.country}
        type={entity.entity_type}
        id={entity.subdomain}
        link={`${currentAppPathname}/${entity.subdomain}${pagePath}`}
        showEntitySettingsButton={showEntitySettingsButton}
      />
    );
  });

  return (
    <Modal.Work
      width="wide"
      onClose={onClose}
      title="Switch entity"
      description={
        <>
          {canCreateEntity && createEntityLink ? (
            <>
              Select an existing entity or{' '}
              <Link.Standard href={createEntityLink} onClick={onCreateEntity} text="create a new entity" />.
            </>
          ) : null}
        </>
      }
    >
      <div className={styles.inputWrapper}>
        <Input.Text placeholder="Filter entities" onChange={setSearchText} />
      </div>
      <div className={isLoading ? styles.loadingSpinnerWrapper : styles.contentWrapper}>
        {isLoading && <LoadingSpinner variant="page" message="Loading Entities" />}
        {noResults && <EntitySwitcherNoResults showCreateEntity={canCreateEntity} onCreateEntity={onCreateEntity} />}
        {!noResults && !isLoading && (
          <>
            <ListTable variant="condensed">
              {!!filteredEntities.length && (
                <ListTable.Header>
                  <ListTable.HeaderCell>Entity</ListTable.HeaderCell>
                  <ListTable.HeaderCell>Type</ListTable.HeaderCell>
                  <ListTable.HeaderCell>ID</ListTable.HeaderCell>
                  {showEntitySettingsButton && <div className={styles.entitySettingBtnHeader} />}
                </ListTable.Header>
              )}
              {entitySwitcherItems}
            </ListTable>
            <Pagination
              currentPage={currentPageNumber}
              rowsPerPage={defaultPageSize}
              totalRows={filteredEntities.length}
              itemName="Entities"
              onPageChange={setCurrentPageNumber}
              onRowsPerPageChange={setPageSize}
            />
          </>
        )}
      </div>
    </Modal.Work>
  );
};
